/**
 * File: refreshTokenManager.ts
 * Description: In-memory cache and sessionStorage persistence for the OAuth
 *              refresh token, with allow-list sanitisation of the value that
 *              is written to browser storage.
 */
import { logger } from '../utils/logger';
const REFRESH_TOKEN_STORAGE_KEY = 'archie_refresh_token';

/** Guards against implausibly sized tokens before they reach sessionStorage. */
const MAX_REFRESH_TOKEN_LENGTH = 8192;

/**
 * Characters a refresh token may contain. Entra ID issues opaque tokens made
 * of base64/base64url characters and segment separators. The stored value is
 * rebuilt from this constant, so no externally controlled data is written to
 * browser storage (SonarQube tssecurity:S8475).
 */
const ALLOWED_REFRESH_TOKEN_CHARACTERS = [
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  'abcdefghijklmnopqrstuvwxyz',
  '0123456789',
  '-._~+/=',
].join('');

let refreshToken: string | null = null;

/**
 * Rebuild a token using only characters read from the constant allow-list.
 *
 * @param token - Candidate refresh token supplied by the auth backend.
 * @returns The rebuilt token, or `null` when it is empty, exceeds
 *          `MAX_REFRESH_TOKEN_LENGTH` or contains a disallowed character.
 */
const sanitizeRefreshToken = (token: string): string | null => {
  if (token.length === 0 || token.length > MAX_REFRESH_TOKEN_LENGTH) {
    return null;
  }

  let sanitized = '';
  for (let index = 0; index < token.length; index += 1) {
    const position = ALLOWED_REFRESH_TOKEN_CHARACTERS.indexOf(
      token.charAt(index),
    );
    if (position === -1) {
      return null;
    }
    sanitized += ALLOWED_REFRESH_TOKEN_CHARACTERS.charAt(position);
  }

  return sanitized;
};

/**
 * Read the refresh token previously persisted in sessionStorage.
 *
 * @returns The stored token, or `null` when absent or malformed.
 */
const readRefreshTokenFromSession = (): string | null => {
  const storedToken = sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  return storedToken ? sanitizeRefreshToken(storedToken) : null;
};

/**
 * Cache the refresh token in memory and persist it to sessionStorage.
 *
 * @param token - Token to store, or `null` to clear the current token.
 */
export const setRefreshToken = (token: string | null): void => {
  if (token === null) {
    refreshToken = null;
    sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    return;
  }

  const sanitizedToken = sanitizeRefreshToken(token);
  if (!sanitizedToken) {
    logger.error('Rejected malformed refresh token');
    refreshToken = null;
    sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    return;
  }

  refreshToken = sanitizedToken;
  sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, sanitizedToken);
};

/**
 * Retrieve the refresh token, hydrating from sessionStorage when needed.
 *
 * @returns The current refresh token, or `null` when none is available.
 */
export const getRefreshToken = (): string | null => {
  refreshToken ??= readRefreshTokenFromSession();

  return refreshToken;
};

/**
 * Remove the refresh token from memory and sessionStorage.
 */
export const clearRefreshToken = (): void => {
  refreshToken = null;
  sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
};
